import React, { useState, useRef, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useHousehold } from '../context/HouseholdContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';
// useNavigate is also used inside InspiredByYou below — import once here.
import UnlockCard from '../components/UnlockCard';
import CreditBalance, { pingCreditBalance } from '../components/CreditBalance';

// PoseOverlay lazy-imports tfjs (~12 MB) — split it from the main bundle.
const PoseOverlay = React.lazy(() => import('../components/PoseOverlay'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
const MAX_MB = 500;

/**
 * Direct-to-S3 video upload.
 *
 * Flow:
 *   1. /api/videos/init             → presigned PUT URL + video_id
 *   2. PUT file to S3 with `Content-Type` matching what we declared
 *   3. /api/videos/{id}/confirm     → moves to `uploaded`, dispatches AI
 *      analysis. If credits are short, returns an unlock_hint we render.
 *
 * Progress is wired through XHR upload events so the user sees a real
 * percentage even on slow networks.
 */
export default function VideoUploadPage() {
  const { token } = useAuth();
  const { activeAthlete, athletes } = useHousehold();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [sessionType, setSessionType] = useState('batting');
  const [drillTag, setDrillTag] = useState('');
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | uploading | confirming | done
  const [result, setResult] = useState(null);
  const [unlockHint, setUnlockHint] = useState(null);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [showPose, setShowPose] = useState(false);
  const [relatedClips, setRelatedClips] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!activeAthlete) {
      const t = setTimeout(() => {
        if (!activeAthlete) navigate('/dashboard');
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [activeAthlete, navigate]);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error('Pick an mp4, mov, m4v, or webm file');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`That file is over ${MAX_MB} MB`);
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file || !activeAthlete) return;
    setStatus('uploading');
    setUnlockHint(null);
    setProgress(0);

    try {
      const init = await axios.post(`${API}/videos/init`, {
        athlete_id: activeAthlete.id,
        content_type: file.type,
        size_bytes: file.size,
        session_type: sessionType,
        drill_tag: drillTag || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const { video_id, presigned_url } = init.data;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presigned_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`S3 PUT failed: ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('S3 PUT network error'));
        xhr.send(file);
      });

      setStatus('confirming');
      const confirm = await axios.post(
        `${API}/videos/${video_id}/confirm`,
        { athlete_id: activeAthlete.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(confirm.data.video);
      pingCreditBalance();
      if (confirm.data.unlock_hint) {
        setUnlockHint(confirm.data.unlock_hint);
        toast.info('Uploaded — analysis paused until you top up.');
      } else if (confirm.data.analysis_dispatched) {
        toast.success('Uploaded — AI is reviewing your session.');
      }
      // Fetch a presigned playback URL so the pose overlay can stream the
      // clip back. The backend attaches it on the video doc when status
      // is uploaded/processing/ready.
      try {
        const detail = await axios.get(
          `${API}/videos/${video_id}?athlete_id=${activeAthlete.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (detail.data?.playback_url) {
          setPlaybackUrl(detail.data.playback_url);
        }
      } catch (err) { console.warn('Could not fetch playback URL (non-blocking):', err); }
      // Inspired by you — fetch up to 3 pro clips of the same technique.
      try {
        const related = await axios.get(
          `${API}/clips/related-to-video/${video_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (related.data?.clips?.length) {
          setRelatedClips(related.data);
        }
      } catch (err) { console.warn('Could not fetch related clips (non-blocking):', err); }
      setStatus('done');
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(detail);
      setStatus('idle');
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <header className="max-w-3xl mx-auto px-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate('/dashboard')} className="opacity-70 hover:opacity-100">← Back</button>
        <Logo size={28} />
        <div className="flex-1" />
        <CreditBalance />
        <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Record a session</h1>
          <p className="text-sm opacity-70 mt-1">
            Up to {MAX_MB} MB. Pick a clip and we'll get AI feedback to you in
            ~2 minutes. Costs 25 credits.
          </p>
        </div>

        <Card theme={theme}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium opacity-80 mb-1.5">Athlete</label>
              <div className="text-base font-semibold">
                {activeAthlete?.display_name || activeAthlete?.full_name || '—'}
              </div>
              {athletes && athletes.length > 1 && (
                <p className="text-xs opacity-60 mt-0.5">Switch athletes from the dashboard.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Session type" value={sessionType}
                onChange={setSessionType}
                options={[
                  ['batting','Batting'], ['bowling','Bowling'],
                  ['fielding','Fielding'], ['fitness','Fitness'],
                ]}
              />
              <Input
                label="Drill tag (optional)" value={drillTag} onChange={setDrillTag}
                placeholder="cover_drive"
              />
            </div>

            <div>
              <label className="block text-sm font-medium opacity-80 mb-1.5">Clip</label>
              <button
                onClick={() => inputRef.current?.click()}
                disabled={status === 'uploading'}
                className="w-full py-8 rounded-xl border-2 border-dashed text-sm"
                style={{
                  borderColor: 'rgba(0,201,167,0.4)',
                  background: 'rgba(0,201,167,0.04)',
                }}
              >
                {file ? (
                  <div>
                    <div className="font-semibold">{file.name}</div>
                    <div className="text-xs opacity-60 mt-1">
                      {(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type}
                    </div>
                  </div>
                ) : (
                  <div className="opacity-70">📹 Tap to pick a clip from your device</div>
                )}
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handlePick}
              />
            </div>

            {status === 'uploading' && (
              <div>
                <div className="text-xs opacity-70 mb-1">Uploading… {progress}%</div>
                <div className="h-2 rounded-full overflow-hidden"
                     style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div
                    className="h-full"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #00A651, #00D9FF)',
                      transition: 'width 0.2s',
                    }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={upload}
              disabled={!file || status !== 'idle'}
              className="w-full py-3 rounded-xl font-semibold text-white"
              style={{
                background: !file || status !== 'idle'
                  ? 'rgba(150,160,180,0.3)'
                  : 'linear-gradient(135deg, #00A651, #00D9FF)',
              }}
            >
              {status === 'uploading'  ? `Uploading… ${progress}%` :
               status === 'confirming' ? 'Finalizing…' :
               status === 'done'       ? 'Uploaded — record another?' :
               '⚡ Upload and analyze (25 credits)'}
            </button>
          </div>
        </Card>

        {unlockHint && <UnlockCard hint={unlockHint} onClose={() => setUnlockHint(null)} />}

        {result && (
          <Card theme={theme}>
            <div className="text-xs uppercase tracking-widest opacity-60 font-bold mb-2">
              Upload status
            </div>
            <div className="text-sm opacity-90">
              State: <strong>{result.status}</strong>. {result.analysis
                ? 'AI analysis is ready below.'
                : 'AI analysis runs in the background — refresh the dashboard in 1-2 min.'}
            </div>

            {playbackUrl && (
              <button
                onClick={() => setShowPose(true)}
                className="mt-3 px-3.5 py-2 rounded-lg text-sm font-semibold text-white"
                style={{
                  background: 'linear-gradient(135deg, #00A651, #00D9FF)',
                }}
              >
                🧍 Show pose overlay
              </button>
            )}

            {result.analysis && <AnalysisBlock analysis={result.analysis} />}
          </Card>
        )}

        {relatedClips && relatedClips.clips?.length > 0 && (
          <InspiredByYou data={relatedClips} theme={theme} />
        )}
      </main>

      {showPose && playbackUrl && (
        <Suspense fallback={null}>
          <PoseOverlay
            src={playbackUrl}
            onClose={() => setShowPose(false)}
            videoId={result?.id}
            athleteId={activeAthlete?.id}
            authToken={token}
          />
        </Suspense>
      )}
    </div>
  );
}

function InspiredByYou({ data, theme }) {
  const navigate = useNavigate();
  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: 'linear-gradient(135deg, rgba(0,166,81,0.10), rgba(0,217,255,0.08))',
        border: '1px solid rgba(0,201,167,0.25)',
      }}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div
            className="text-[10px] uppercase tracking-widest font-bold"
            style={{ color: '#00C9A7' }}
          >
            ✨ Inspired by you
          </div>
          <h2 className="font-bold mt-0.5">Watch how the pros do it</h2>
        </div>
        {data.technique && (
          <span
            className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded shrink-0"
            style={{ background: 'rgba(0,201,167,0.18)', color: '#00C9A7' }}
          >
            {data.technique.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {data.clips.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/clips?focus=${c.id}`)}
            className="text-left rounded-xl overflow-hidden transition-transform hover:-translate-y-0.5"
            style={{
              background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(150,160,180,0.2)',
            }}
          >
            <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000' }}>
              {c.thumbnail_url && (
                <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
              )}
              <span
                className="absolute bottom-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}
              >
                {c.duration_seconds || 30}s
              </span>
            </div>
            <div className="p-2.5">
              <div className="text-xs font-semibold leading-tight line-clamp-2">
                {c.title}
              </div>
              {c.player?.name && (
                <div className="text-[11px] opacity-60 mt-0.5 truncate">
                  {c.player.name}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function AnalysisBlock({ analysis }) {
  return (
    <div className="mt-3 space-y-3 text-sm">
      {analysis.summary && <p className="opacity-90">{analysis.summary}</p>}
      {(analysis.expected_strengths || []).length > 0 && (
        <Section title="What to look for (strengths)">
          {analysis.expected_strengths.map((s, i) => <li key={`strength-${i}-${s.slice(0, 24)}`}>{s}</li>)}
        </Section>
      )}
      {(analysis.common_errors || []).length > 0 && (
        <Section title="Common errors">
          {analysis.common_errors.map((s, i) => <li key={`error-${i}-${s.slice(0, 24)}`}>{s}</li>)}
        </Section>
      )}
      {(analysis.followup_drills || []).length > 0 && (
        <Section title="Follow-up drills">
          {analysis.followup_drills.map((s, i) => <li key={`drill-${i}-${s.slice(0, 24)}`}>{s}</li>)}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest opacity-60 font-bold mb-1">{title}</div>
      <ul className="list-disc pl-5 space-y-1">{children}</ul>
    </div>
  );
}

function Card({ children, theme }) {
  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      {children}
    </section>
  );
}

function Input({ label, value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium opacity-80 mb-1.5">{label}</label>
      <input
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg outline-none"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(150,160,180,0.25)',
          color: 'inherit',
        }}
        {...rest}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-medium opacity-80 mb-1.5">{label}</label>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(150,160,180,0.25)',
          color: 'inherit',
        }}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
